import lodash from 'lodash'
import { Logger } from 'wdc-cube'
import { NumberFilter, ObjectProperty } from '../tcm_filter_manager'
import { JunctionMode, NumberFilterScope, NumberOperator, NumberParcelFilterScope } from '../tcm_scopes'
import { FilterPaneOwner, FilterPresenter } from './tcm_filter_types'

const LOG = Logger.get('TCM-NumberFilter')

let UID_GEN = 0

export class TcmPresenterForNumberFilter extends FilterPresenter<NumberFilterScope, FilterPaneOwner> {
    // Constructor

    constructor(owner: FilterPaneOwner, scope: NumberFilterScope, valueProperty: ObjectProperty<NumberFilter>) {
        super(owner, scope)
        this.valueProperty = valueProperty
        this.fetch = lodash.debounce(() => {
            this.owner.owner.fetchData('').catch(LOG.caught)
        }, 150)
    }

    // :: Fields

    private valueProperty: ObjectProperty<NumberFilter>

    private fetch: lodash.DebouncedFuncLeading<() => void>

    // :: Getters and Setters

    get owner() {
        return super.owner as FilterPaneOwner
    }

    get size() {
        return this.scope.conjunction.length + this.scope.disjunction.length
    }

    protected override async bindEvents() {
        this.scope.onAddFilter = this.action(this.handleAddFilter)
        this.scope.onClearAllFilters = this.action(this.handleClearAllFilters)
        LOG.debug('initialized')
    }

    override release(): void {
        this.fetch.cancel()
    }

    override clear() {
        this.scope.conjunction.length = 0
        this.scope.disjunction.length = 0
        this.valueProperty.clear()
    }

    override publish() {
        this.valueProperty.clear()

        const isRange = (scope: NumberParcelFilterScope) =>
            scope.operator === NumberOperator.in_range || scope.operator === NumberOperator.out_of_range
        const emptyPredicate = (scope: NumberParcelFilterScope) =>
            lodash.trim(scope.value) === '' && !(isRange(scope) && lodash.trim(scope.toValue) !== '')
        this.scope.conjunction.removeByCriteria(emptyPredicate)
        this.scope.disjunction.removeByCriteria(emptyPredicate)

        for (const parcelScope of [...this.scope.conjunction, ...this.scope.disjunction]) {
            const value = parcelScope.value.trim()
            if (emptyPredicate(parcelScope)) {
                continue
            }

            const filter: NumberFilter = {
                mode: parcelScope.junctionMode,
                operator: parcelScope.operator,
                value
            }

            if (isRange(parcelScope)) {
                filter.to = parcelScope.toValue
                parcelScope.toExclusive && (filter.toExclusive = true)
            }

            this.valueProperty.add(filter)
        }
    }

    add(filter: NumberFilter) {
        const juntionScope = filter.mode == JunctionMode.AND ? this.addConjuntion() : this.addDisjuntion()
        juntionScope.operator = filter.operator
        juntionScope.value = filter.value ? `${filter.value}` : ''
        juntionScope.toValue = filter.to ? `${filter.to}` : ''
        juntionScope.toExclusive = !!filter.toExclusive
    }

    toggleFrom(mode: JunctionMode, value: number, inclusive: boolean) {
        const junction = mode === JunctionMode.AND ? this.scope.conjunction : this.scope.disjunction
        const strValue = `${value}`
        const opearator = inclusive ? NumberOperator.gte : NumberOperator.gt
        const idx = junction.findIndex((itemScope) => {
            return itemScope.junctionMode === mode && itemScope.operator === opearator && itemScope.value === strValue
        })
        if (idx !== -1) {
            junction.removeByIndex(idx)
            return
        }

        this.add({
            mode: mode,
            operator: opearator,
            value: value
        })
    }

    toggleTo(mode: JunctionMode, value: number, inclusive: boolean) {
        const junction = mode === JunctionMode.AND ? this.scope.conjunction : this.scope.disjunction
        const strValue = `${value}`
        const opearator = inclusive ? NumberOperator.lte : NumberOperator.lt
        const idx = junction.findIndex((itemScope) => {
            return itemScope.junctionMode === mode && itemScope.operator === opearator && itemScope.value === strValue
        })
        if (idx !== -1) {
            junction.removeByIndex(idx)
            return
        }

        this.add({
            mode: mode,
            operator: opearator,
            value: value
        })
    }

    toggleRange(mode: JunctionMode, from: number, to: number) {
        const junction = mode === JunctionMode.AND ? this.scope.conjunction : this.scope.disjunction
        const strFromValue = `${from}`
        const strToValue = `${to}`
        const idx = junction.findIndex((itemScope) => {
            return (
                itemScope.junctionMode === mode &&
                itemScope.operator === NumberOperator.in_range &&
                itemScope.value === strFromValue &&
                itemScope.toValue === strToValue
            )
        })

        if (idx !== -1) {
            junction.removeByIndex(idx)
            return
        }

        this.add({
            mode: mode,
            operator: NumberOperator.in_range,
            value: from,
            to: to,
            toExclusive: true
        })
    }

    addConjuntion() {
        const andScope = new NumberParcelFilterScope()
        andScope.uid = `and-${UID_GEN++}`
        andScope.junctionMode = JunctionMode.AND
        andScope.operator = NumberOperator.is
        andScope.onOperatorChange = this.action(this.handleOperatorChange.bind(this, andScope))
        andScope.onValueChange = this.action(this.handleValueChange.bind(this, andScope))
        andScope.onClear = this.action(this.handleClear.bind(this, andScope))
        andScope.onEnterKeyPressed = this.action(this.handleEnterKeyPressed)
        andScope.update = this.update

        this.scope.conjunction.push(andScope)
        return andScope
    }

    addDisjuntion() {
        const orScope = new NumberParcelFilterScope()
        orScope.uid = `or-${UID_GEN++}`
        orScope.junctionMode = JunctionMode.OR
        orScope.operator = NumberOperator.is
        orScope.onOperatorChange = this.action(this.handleOperatorChange.bind(this, orScope))
        orScope.onValueChange = this.action(this.handleValueChange.bind(this, orScope))
        orScope.onClear = this.action(this.handleClear.bind(this, orScope))
        orScope.onEnterKeyPressed = this.action(this.handleEnterKeyPressed)
        orScope.update = this.update
        this.scope.disjunction.push(orScope)
        return orScope
    }

    private async handleClearAllFilters() {
        this.clear()
    }

    private async handleAddFilter(mode: JunctionMode) {
        this.mostrarItemAdicionado()

        if (mode === JunctionMode.AND) {
            this.addConjuntion()
        } else {
            this.addDisjuntion()
        }
    }

    private async handleClear(parcelScope: NumberParcelFilterScope) {
        let found = true

        let idx = this.scope.conjunction.findIndex((item) => item.uid === parcelScope.uid)
        if (idx !== -1) {
            this.scope.conjunction.removeByIndex(idx)
            found = true
        }

        idx = this.scope.disjunction.findIndex((item) => item.uid === parcelScope.uid)
        if (idx !== -1) {
            this.scope.disjunction.removeByIndex(idx)
            found = true
        }

        if (found && parcelScope.value) {
            this.fetch.cancel()
            this.fetch()
        }
    }

    private async handleOperatorChange(parcelScope: NumberParcelFilterScope, value: NumberOperator) {
        parcelScope.operator = value
        parcelScope.valueVisible = !(value === NumberOperator.is_blank || value === NumberOperator.is_not_blank)
    }

    private async handleValueChange(parcelScope: NumberParcelFilterScope, value: string, source: 'from' | 'to') {
        if (source === 'from') {
            parcelScope.value = value
        } else {
            parcelScope.toValue = value
        }
        parcelScope.toExclusive = false
    }

    private async handleEnterKeyPressed() {
        this.fetch.cancel()
        this.fetch()
    }

    apply(filters: NumberFilter[] | null | undefined) {
        if (!filters || filters.length === 0) {
            return
        }

        filters.forEach((filter) => {
            const hasTo = filter.to !== null && filter.to !== undefined && filter.to !== ''
            if ((filter.value === null || filter.value === '' || filter.value === undefined) && !hasTo) {
                return
            }

            let itemScope: NumberParcelFilterScope
            if (filter.mode === JunctionMode.AND) {
                itemScope = this.addConjuntion()
            } else {
                itemScope = this.addDisjuntion()
            }

            if (filter.value) {
                itemScope.value = `${filter.value}`
            }

            if (filter.to) {
                itemScope.toValue = `${filter.to}`
            }

            itemScope.operator = filter.operator
            itemScope.toExclusive = !!filter.toExclusive
        })
    }
}
